import * as React from 'react';

import { compose } from 'recompose';

import { useLocation } from 'js/lib/useRouter';
import user, { isAuthenticatedUser } from 'js/lib/user';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { allContractsHardPausedOrTerminated } from 'bundles/enterprise-admin-contracts/enterprise-admin-contracts-index';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import ProgramArchivedDenyModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramArchivedDenyModal';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useEnterpriseContracts,
  useIsProgramLoading,
  useProgram,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useIsJitOrganization } from 'bundles/unified-common/hooks/useIsJitOrganization';
import { useSsoLockedOutStatus } from 'bundles/unified-common/hooks/useSsoLockedOutStatus';
import { normalizeSearchString, searchQueryWithoutSource } from 'bundles/unified-common/utils/orgSearchQuery';
import { isWithinSsoGracePeriod } from 'bundles/unified-common/utils/ssoGracePeriod';

type Props = {
  naptime?: InjectedNaptime;
  children: React.ReactNode;
};

export const EnterprisePageGuard = ({ children, naptime }: Props): React.ReactNode | null => {
  const program = useProgram();
  const enterpriseContracts = useEnterpriseContracts();
  const isProgramLoading = useIsProgramLoading();
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const isFeaturesLoading = useEnterpriseProductFeaturesLoading();
  const thirdPartyOrg = useThirdPartyOrganization();
  const { isSsoLockedOut: isSsoLockedOutAdminSetting, isLoading: isSsoLockedAdminSettingLoading } =
    useSsoLockedOutStatus();

  const isAttemptSSOLogin = location.query.attemptSSOLogin === 'true';
  const isUserAuthenticated = isAuthenticatedUser();

  const { isJit, isLoading: isJitLoading } = useIsJitOrganization({
    orgId: thirdPartyOrg?.id,
    skip: !isAttemptSSOLogin || !isUserAuthenticated,
  });

  // Wait for all required data to load before making decisions
  if (isSsoLockedAdminSettingLoading || (isAttemptSSOLogin && isUserAuthenticated && isJitLoading)) {
    return;
  }

  // Redirect single program pages to org-scoped when unified pages experiment is on.
  const isOrgScopedHome = GrowthDiscoveryEpicClient.preview('orgScopedHome');
  if (
    isOrgScopedHome &&
    !isProgramLoading &&
    program &&
    thirdPartyOrg?.slug &&
    location.pathname.startsWith('/programs/')
  ) {
    const programSlug = program.slug;
    const programPrefix = `/programs/${programSlug}`;
    if (location.pathname === programPrefix || location.pathname.startsWith(programPrefix + '/')) {
      const subpath = location.pathname.slice(programPrefix.length) || '';
      // Program search uses /browse (historical); org uses /search.
      const isProgramSearch = subpath === '/browse' || subpath === '/browse/';
      const orgPath = isProgramSearch
        ? `/organizations/${thirdPartyOrg.slug}/search`
        : `/organizations/${thirdPartyOrg.slug}${subpath}`;
      const searchPart = isProgramSearch
        ? searchQueryWithoutSource(location.search || '')
        : normalizeSearchString(location.search || '');
      redirectClientOrServer(orgPath + searchPart);
      return null;
    }
  }

  const isLearnerPartOfLfoEnabledOrg = location.query.isExternal === 'true';
  const isOrgWhiteListedForSSOLogin =
    features?.whitelistOrganizationForSSOLogin || features?.whitelistOrganizationProgramsForSSOLogin;
  const shouldRedirectToIdpOnOrg =
    isLearnerPartOfLfoEnabledOrg && isSsoLockedOutAdminSetting && isOrgWhiteListedForSSOLogin;

  /**
   * When attempting SSO login, always redirect if user is not authenticated,
   * or if it's a JIT organization and the user is not within the SSO grace period
   */
  const shouldRedirectForAttemptSSO =
    isAttemptSSOLogin && (!isUserAuthenticated || (isJit && !isWithinSsoGracePeriod(thirdPartyOrg?.id ?? '')));
  const shouldRedirectToIdp = shouldRedirectForAttemptSSO || (!isUserAuthenticated && shouldRedirectToIdpOnOrg);

  if (shouldRedirectToIdp && thirdPartyOrg && thirdPartyOrg.loginMethod === 'SAML') {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrg.id} />;
  }

  const shouldRedirectToLoginForLfo =
    !isAuthenticatedUser() &&
    isFeaturesLoading === false &&
    location.query.isExternal &&
    !location.query.attemptSSOLogin;

  if (shouldRedirectToLoginForLfo) {
    redirectClientOrServer('/login?redirectTo=' + encodeURIComponent(location.pathname + location.search));
    return null;
  }

  // Show program archived page if program is archived.
  if (
    !isProgramLoading &&
    program &&
    (program?.endedAt || (enterpriseContracts && allContractsHardPausedOrTerminated(enterpriseContracts)))
  ) {
    return <ProgramArchivedDenyModal userId={user.get()?.id} naptime={naptime} />;
  }

  // If no program found, render NotFound page.
  if (!isProgramLoading && !program) {
    if (inServerContext) {
      throw new NotFoundError();
    } else {
      return <NotFoundPage />;
    }
  }

  return <React.Fragment>{children}</React.Fragment>;
};

/**
 * @description Checks data required to serve an enterprise page, if said data is not present returns appropriate error page.
 */
export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterprisePageGuard);
